import { IconInfo } from "@/assets/icons/geist/IconInfo";
import { Button } from "@/components/flexkit/Button";
import { Input } from "@/components/podkit/forms/Input";
import { PasswordInput } from "@/components/podkit/forms/PasswordInput";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/podkit/select/Select";
import { toast } from "@/components/podkit/toasts/use-toast";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";
import { Text } from "@/components/podkit/typography/Text";
import { Provider } from "@/components/runners/details/types";
import { ANTHROPIC_ENDPOINT } from "@/components/runners/details/llm-integration-utils";
import { Tooltip } from "@/components/Tooltip";
import { SupportedModel } from "gitpod-next-api/gitpod/v1/agent_pb";
import { type FC, useRef, useState } from "react";

type ModelSelectProps = {
    selectedModel: SupportedModel;
    onModelChange: (model: SupportedModel) => void;
    testId?: string;
};

export const ModelSelect: FC<ModelSelectProps> = ({ selectedModel, onModelChange, testId = "llm-model-select" }) => (
    <div className="flex flex-col gap-2">
        <label htmlFor="model-select" className="text-sm font-medium">
            Model
        </label>
        <Select
            value={selectedModel.toString()}
            onValueChange={(value) => onModelChange(parseInt(value) as SupportedModel)}
        >
            <SelectTrigger id="model-select" data-testid={testId} loading={false}>
                <SelectValue />
            </SelectTrigger>
            <SelectContent>
                <SelectItem value={SupportedModel.OPUS_4_6.toString()}>
                    <div className="flex flex-col">
                        <Text className="text-sm font-medium">Claude 4.6 Opus (Recommended)</Text>
                        <Text className="text-xs text-content-secondary">Maximum intelligence for complex tasks</Text>
                    </div>
                </SelectItem>
                <SelectItem value={SupportedModel.OPUS_4_5.toString()}>
                    <div className="flex flex-col">
                        <Text className="text-sm font-medium">Claude 4.5 Opus</Text>
                        <Text className="text-xs text-content-secondary">Previous generation Opus model</Text>
                    </div>
                </SelectItem>
                <SelectItem value={SupportedModel.SONNET_4_5.toString()}>
                    <div className="flex flex-col">
                        <Text className="text-sm font-medium">Claude 4.5 Sonnet</Text>
                        <Text className="text-xs text-content-secondary">Best balance of speed and cost</Text>
                    </div>
                </SelectItem>
                <SelectItem value={SupportedModel.SONNET_4.toString()}>
                    <div className="flex flex-col">
                        <Text className="text-sm font-medium">Claude 4 Sonnet</Text>
                        <Text className="text-xs text-content-secondary">Previous generation model</Text>
                    </div>
                </SelectItem>
                <SelectItem value={SupportedModel.SONNET_3_7.toString()}>
                    <div className="flex flex-col">
                        <Text className="text-sm font-medium">Claude 3.7 Sonnet</Text>
                        <Text className="text-xs text-content-secondary">Legacy model</Text>
                    </div>
                </SelectItem>
            </SelectContent>
        </Select>
        <Text className="text-xs text-content-secondary">We recommend Claude 4.6 Opus for the best performance.</Text>
    </div>
);

type BedrockFieldsProps = {
    modelId: string;
    onModelIdChange: (value: string) => void;
};

export const BedrockFields: FC<BedrockFieldsProps> = ({ modelId, onModelIdChange }) => (
    <div className="flex flex-col gap-2">
        <label htmlFor="bedrock-model" className="text-sm font-medium">
            Bedrock Model ID
        </label>
        <Input
            id="bedrock-model"
            type="text"
            value={modelId}
            onChange={(e) => onModelIdChange(e.target.value)}
            placeholder="eu.anthropic.claude-sonnet-4-20250514-v1:0"
            required
            data-testid="llm-bedrock-model-input"
        />
        <Text className="text-sm text-content-secondary">
            Use foundation model or inference profile ID. Availability varies by region.{" "}
            <ExternalLink
                href="https://docs.aws.amazon.com/bedrock/latest/userguide/inference-profiles-support.html"
                target="_blank"
                rel="noreferrer"
            >
                See all model IDs
            </ExternalLink>
            .
        </Text>
    </div>
);

type VertexFieldsProps = {
    region: string;
    modelVersion: string;
    onRegionChange: (value: string) => void;
    onModelVersionChange: (value: string) => void;
};

export const VertexFields: FC<VertexFieldsProps> = ({ region, modelVersion, onRegionChange, onModelVersionChange }) => (
    <div className="flex flex-col gap-2">
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            <div className="flex flex-col gap-2">
                <label htmlFor="vertex-region" className="text-sm font-medium">
                    Region
                </label>
                <Input
                    id="vertex-region"
                    type="text"
                    value={region}
                    onChange={(e) => onRegionChange(e.target.value)}
                    placeholder="global"
                    required
                    data-testid="llm-vertex-region-input"
                />
            </div>
            <div className="flex flex-col gap-2">
                <label htmlFor="vertex-model" className="text-sm font-medium">
                    Model Version
                </label>
                <Input
                    id="vertex-model"
                    type="text"
                    value={modelVersion}
                    onChange={(e) => onModelVersionChange(e.target.value)}
                    placeholder="claude-sonnet-4@20250514"
                    required
                    data-testid="llm-vertex-model-input"
                />
            </div>
        </div>
        <Text className="text-sm text-content-secondary">
            Use <span className="font-medium">global</span> region for best availability.{" "}
            <ExternalLink
                href="https://docs.anthropic.com/en/api/claude-on-vertex-ai#model-availability"
                target="_blank"
                rel="noreferrer"
            >
                See model availability
            </ExternalLink>
            .
        </Text>
    </div>
);

type EndpointFieldsProps = {
    endpoint: string;
    onEndpointChange: (value: string) => void;
    provider: Provider;
};

export const EndpointFields: FC<EndpointFieldsProps> = ({ endpoint, onEndpointChange, provider }) => (
    <div className="flex flex-col gap-2">
        <label htmlFor="endpoint" className="text-sm font-medium">
            Endpoint URL
        </label>
        <Input
            id="endpoint"
            type="url"
            value={endpoint}
            onChange={(e) => onEndpointChange(e.target.value)}
            placeholder={ANTHROPIC_ENDPOINT}
            disabled={provider === Provider.Anthropic}
            required
            data-testid="llm-endpoint-input"
        />
        <Text className="text-sm text-content-secondary">
            {provider === Provider.Anthropic ? (
                <span>Endpoint is fixed for Anthropic and cannot be changed</span>
            ) : (
                <span>The base URL for your LLM provider&apos;s API</span>
            )}
        </Text>
    </div>
);

type VertexServiceAccountProps = {
    uploadedFileName: string | null;
    onFileUpload: (content: string, fileName: string) => void;
    onFileRemove: () => void;
    isEdit?: boolean;
};

export const VertexServiceAccount: FC<VertexServiceAccountProps> = ({
    uploadedFileName,
    onFileUpload,
    onFileRemove,
    isEdit = false,
}) => {
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [isDragging, setIsDragging] = useState(false);

    const handleFile = async (file: File) => {
        try {
            const text = await file.text();
            JSON.parse(text); // Validate JSON
            onFileUpload(text, file.name);
            toast({ title: "Service account loaded" });
        } catch {
            toast({ title: "Invalid file", description: "Please provide a valid JSON service account" });
        }
    };

    return (
        <div className="flex flex-col gap-2">
            <label className="flex items-center gap-2 text-sm font-medium">
                <span>{isEdit ? "Service Account JSON (Optional)" : "Service Account JSON"}</span>
                <Tooltip
                    content={
                        <span>
                            {isEdit
                                ? "Upload a new service account to replace the existing one"
                                : "You must attach a service account to use Google Vertex"}
                        </span>
                    }
                >
                    <span className="inline-flex items-center text-content-tertiary">
                        <IconInfo size="base" />
                    </span>
                </Tooltip>
            </label>
            {uploadedFileName ? (
                <div className="flex items-center justify-between rounded-lg border border-border-base bg-surface-secondary p-3">
                    <Text className="truncate pr-4 font-mono text-sm">{uploadedFileName}</Text>
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={onFileRemove}
                        aria-label="Remove uploaded file"
                        data-tracking-id="remove-uploaded-file-llm-integration-components"
                    >
                        ×
                    </Button>
                </div>
            ) : (
                <>
                    <div
                        onDragOver={(e) => {
                            e.preventDefault();
                            setIsDragging(true);
                        }}
                        onDragLeave={() => setIsDragging(false)}
                        onDrop={async (e) => {
                            e.preventDefault();
                            setIsDragging(false);
                            const file = e.dataTransfer.files?.[0];
                            if (file) {
                                await handleFile(file);
                            }
                        }}
                        className={`flex items-center justify-between rounded-lg bg-surface-secondary p-3 ${
                            isDragging ? "border border-content-primary" : "border border-border-base"
                        }`}
                        aria-label="Upload service account JSON"
                    >
                        <div className="flex flex-col">
                            <Text className="text-sm font-medium">Drag & drop service account JSON</Text>
                            <Text className="text-xs text-content-secondary">or choose a file</Text>
                        </div>
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={() => fileInputRef.current?.click()}
                            data-tracking-id="choose-file-llm-integration-components"
                        >
                            Choose file
                        </Button>
                    </div>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="application/json,.json"
                        className="hidden"
                        aria-label="Upload service account JSON"
                        onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            await handleFile(file);
                            e.currentTarget.value = "";
                        }}
                    />
                    <Text className="text-sm text-content-secondary">
                        {isEdit
                            ? "Upload a new service account JSON to replace the existing one (leave empty to keep current account)."
                            : "Upload the Google Cloud service account JSON (encrypted and stored securely)."}
                    </Text>
                </>
            )}
        </div>
    );
};

type ApiKeyFieldProps = {
    apiKey: string;
    onApiKeyChange: (value: string) => void;
    provider: Provider;
    isEdit?: boolean;
};

export const ApiKeyField: FC<ApiKeyFieldProps> = ({ apiKey, onApiKeyChange, provider, isEdit = false }) => (
    <div className="flex flex-col gap-2">
        <label htmlFor="api-key" className="text-sm font-medium">
            {isEdit ? "API Key (Optional)" : "API Key"}
        </label>
        <PasswordInput
            id="api-key"
            value={apiKey}
            onChange={(e) => onApiKeyChange(e.target.value)}
            placeholder={
                provider === Provider.Bedrock
                    ? "Not required for Bedrock"
                    : isEdit
                      ? "Leave empty to keep existing key"
                      : "Enter your API key"
            }
            required={!isEdit && provider !== Provider.Bedrock}
            revealable={true}
            data-testid="llm-api-key-input"
            disabled={provider === Provider.Bedrock}
        />
        <Text className="text-sm text-content-secondary">
            {provider === Provider.Bedrock
                ? "Bedrock uses AWS credentials; no API key is required."
                : isEdit
                  ? "Enter a new API key to replace the existing one (leave empty to keep current key)"
                  : "Your LLM provider's API key (encrypted and stored securely)"}
        </Text>
    </div>
);

type ProviderFieldsProps = {
    provider: Provider;
    bedrockModelId: string;
    onBedrockModelIdChange: (value: string) => void;
    vertexRegion: string;
    onVertexRegionChange: (value: string) => void;
    vertexModelVersion: string;
    onVertexModelVersionChange: (value: string) => void;
    endpoint: string;
    onEndpointChange: (value: string) => void;
    uploadedFileName: string | null;
    onFileUpload: (content: string, fileName: string) => void;
    onFileRemove: () => void;
    apiKey: string;
    onApiKeyChange: (value: string) => void;
    isEdit?: boolean;
};

export const ProviderFields: FC<ProviderFieldsProps> = ({
    provider,
    bedrockModelId,
    onBedrockModelIdChange,
    vertexRegion,
    onVertexRegionChange,
    vertexModelVersion,
    onVertexModelVersionChange,
    endpoint,
    onEndpointChange,
    uploadedFileName,
    onFileUpload,
    onFileRemove,
    apiKey,
    onApiKeyChange,
    isEdit = false,
}) => (
    <>
        {/* Provider-specific endpoint/configuration fields */}
        {provider === Provider.Bedrock ? (
            <BedrockFields modelId={bedrockModelId} onModelIdChange={onBedrockModelIdChange} />
        ) : provider === Provider.Vertex ? (
            <VertexFields
                region={vertexRegion}
                modelVersion={vertexModelVersion}
                onRegionChange={onVertexRegionChange}
                onModelVersionChange={onVertexModelVersionChange}
            />
        ) : (
            <EndpointFields endpoint={endpoint} onEndpointChange={onEndpointChange} provider={provider} />
        )}

        {/* Authentication fields */}
        {provider === Provider.Vertex ? (
            <VertexServiceAccount
                uploadedFileName={uploadedFileName}
                onFileUpload={onFileUpload}
                onFileRemove={onFileRemove}
                isEdit={isEdit}
            />
        ) : (
            <ApiKeyField apiKey={apiKey} onApiKeyChange={onApiKeyChange} provider={provider} isEdit={isEdit} />
        )}
    </>
);
